import { useEffect, useMemo, useState } from 'react';
import * as db from '../lib/data.js';
import { Modal, Fld } from './Modals.jsx';

// ใบสั่งซื้อ ↔ ของที่มาถึง (deploy-28) — ตอบ 2 คำถามที่ลืมกันทุกปี:
//   "สั่งไปกี่ชิ้น" (ใบสั่งอยู่ถาวรกับแบรนด์ ไม่ผูกตู้ · สั่งปีที่แล้วมาส่งปีนี้ก็ยังอยู่)
//   "ราคานี้จริงรึป่าว" (เทียบราคาต่อชิ้นกับที่ตกลง + ความจำราคาย้อนหลัง)
// ⚠ เทียบที่ "ต่อชิ้น" เสมอ — PI บางเจ้าให้ราคาต่อกล่อง (Nalgene 1 กล่อง = 6 ขวด)

const TH_MON = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const fmtDate = (ymd) => {                       // order_date เป็นชนิด date (ไม่มีเวลา) — ไม่ต้องแปลงโซนเวลา
  if (!ymd) return '—';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '—';
  return `${d} ${TH_MON[m - 1]} ${String((y + 543) % 100).padStart(2, '0')}`;
};
const CUR = { JPY: '¥', USD: '$', EUR: '€', CNY: '¥', KRW: '₩', THB: '฿', GBP: '£', TWD: 'NT$' };
const money = (n, c) => n == null || n === '' ? '—'
  : (CUR[c] ?? (c ? c + ' ' : '')) + Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
const qty = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
const pct = (a, b) => (b ? Math.round((a - b) / b * 1000) / 10 : null);
const near = (a, b) => a != null && b != null && Math.abs(a - b) <= Math.abs(b) * 0.005 + 0.005;

const STATUS_TH = { open: 'กำลังรอของ', closed: 'ปิดแล้ว', cancelled: 'ยกเลิก' };

// ── หัวใบสั่งซื้อ ─────────────────────────────────────────────────────────────
function PoModal({ item, defCurrency, onClose, onSave, onDelete }) {
  const [f, setF] = useState({
    po_no: item?.po_no ?? '', supplier_ref: item?.supplier_ref ?? '',
    order_date: item?.order_date ?? '', expected_ship: item?.expected_ship ?? '',
    currency: item?.currency ?? defCurrency ?? '', incoterm: item?.incoterm ?? '',
    season: item?.season ?? '', status: item?.status ?? 'open', note: item?.note ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await onSave({
        po_no: f.po_no.trim() || null, supplier_ref: f.supplier_ref.trim() || null,
        order_date: f.order_date || null, expected_ship: f.expected_ship || null,
        currency: f.currency.trim().toUpperCase().slice(0, 3) || null,
        incoterm: f.incoterm.trim() || null, season: f.season.trim() || null,
        status: f.status, note: f.note.trim() || null,
      });
      onClose();
    } catch (e) { setErr(e.message ?? String(e)); setBusy(false); }
  };
  return (
    <Modal title={item ? 'แก้ใบสั่งซื้อ' : 'ใบสั่งซื้อใหม่'}
      sub="ใส่เท่าที่รู้ก่อน กลับมาเติมทีหลังได้ — วันที่สั่งคือตัวที่ทำให้ออเดอร์ข้ามปีไม่หาย" onClose={onClose}>
      {err && <div className="ferr">{err}</div>}
      <div className="frow">
        <Fld label="วันที่สั่ง" hint="สั่งวันไหน — ใช้เรียงและบอกว่าเป็นออเดอร์ของปีไหน">
          <input type="date" value={f.order_date} onChange={set('order_date')}/></Fld>
        <Fld label="ซัพพลายเออร์นัดส่ง" hint="ถ้ารู้กำหนดคร่าวๆ ใส่ไว้ จะได้รู้ว่าเลยนัดไปนานแค่ไหน">
          <input type="date" value={f.expected_ship} onChange={set('expected_ship')}/></Fld>
      </div>
      <div className="frow">
        <Fld label="เลขใบสั่งซื้อ (ฝั่งเรา)" hint="ถ้าไม่มีเลข เว้นไว้ได้">
          <input value={f.po_no} onChange={set('po_no')} placeholder="PO-2568-014"/></Fld>
        <Fld label="เลข PI / order confirm (ฝั่งเขา)" hint="เลขบนใบที่ซัพพลายเออร์ส่งกลับมา">
          <input value={f.supplier_ref} onChange={set('supplier_ref')} placeholder="PI-9931"/></Fld>
      </div>
      <div className="frow">
        <Fld label="สกุลเงิน"><input value={f.currency} onChange={set('currency')} placeholder="JPY" maxLength={3}/></Fld>
        <Fld label="Incoterm"><input value={f.incoterm} onChange={set('incoterm')} placeholder="FOB"/></Fld>
        <Fld label="คอลเลกชัน / ฤดู" hint="เช่น FW25, SS26"><input value={f.season} onChange={set('season')}/></Fld>
      </div>
      <Fld label="สถานะ" hint="ปิดแล้ว = ของมาครบหรือเลิกรอแล้ว จะไม่ไปนับเป็นของค้าง">
        <select value={f.status} onChange={set('status')}>
          {Object.entries(STATUS_TH).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select></Fld>
      <Fld label="หมายเหตุ"><textarea rows={2} value={f.note} onChange={set('note')}
        placeholder="เช่น ตกลงราคาทางอีเมล 3 มี.ค. · แถมอะไหล่ 5 ชุด"/></Fld>
      <div className="mfoot spread">
        <div className="mfoot-l">{item && onDelete && <button className="btn-del" onClick={onDelete}>ลบใบนี้</button>}</div>
        <div className="mfoot-r">
          <button className="btn2" onClick={onClose}>ยกเลิก</button>
          <button className="btn1" disabled={busy} onClick={submit}>{busy ? 'กำลังบันทึก…' : 'บันทึก'}</button>
        </div>
      </div>
    </Modal>
  );
}

// ── บรรทัดที่สั่ง: กรอกในตาราง หรือวางจากไฟล์/Excel ทีเดียว ────────────────────
const blank = () => ({ _k: Math.random().toString(36).slice(2), sku: '', description: '', qty: '', unit: '', pack_size: 1, unit_price: '', note: '' });

function PoLinesModal({ po, lines, currency, onClose, onSave }) {
  const [rows, setRows] = useState(() => (lines.length
    ? lines.map(l => ({ _k: l.id, id: l.id, sku: l.sku ?? '', description: l.description ?? '', qty: l.qty ?? '',
        unit: l.unit ?? '', pack_size: l.pack_size ?? 1, unit_price: l.unit_price ?? '', note: l.note ?? '' }))
    : [blank(), blank(), blank()]));
  const [paste, setPaste] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, field) => (e) => setRows(rs => rs.map(r => r._k === k ? { ...r, [field]: e.target.value } : r));
  const del = (k) => setRows(rs => rs.filter(r => r._k !== k));

  // วางจาก Excel/CSV: sku, จำนวน, ราคา/หน่วย, ชิ้นต่อหน่วย, ชื่อ  (คั่นด้วย tab หรือ comma)
  const applyPaste = () => {
    const out = [];
    for (const raw of paste.split('\n')) {
      const line = raw.trim(); if (!line) continue;
      const c = line.split(/\t|,(?=(?:[^"]*"[^"]*")*[^"]*$)/).map(x => x.trim().replace(/^"|"$/g, ''));
      if (/^(sku|รหัส|code|item)/i.test(c[0] ?? '')) continue;      // ข้ามหัวตาราง
      const num = (v) => { const n = Number(String(v ?? '').replace(/[, ]/g, '')); return Number.isFinite(n) ? n : ''; };
      out.push({ ...blank(), sku: c[0] ?? '', qty: num(c[1]), unit_price: num(c[2]),
        pack_size: num(c[3]) || 1, description: c[4] ?? '' });
    }
    if (!out.length) { setErr('ไม่พบบรรทัดที่อ่านได้ — 1 บรรทัดต่อ 1 รายการ เรียง: รหัส, จำนวน, ราคา/หน่วย, ชิ้นต่อหน่วย, ชื่อ'); return; }
    setErr(null);
    setRows(rs => [...rs.filter(r => r.sku || r.description || r.qty), ...out]);
    setPaste('');
  };

  const clean = rows.filter(r => (r.sku || '').trim() || (r.description || '').trim() || Number(r.qty) > 0);
  const totPieces = clean.reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.pack_size) || 1), 0);
  const totValue = clean.reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.unit_price) || 0), 0);

  const submit = async () => {
    setBusy(true); setErr(null);
    try { await onSave(clean); onClose(); }
    catch (e) { setErr(e.message ?? String(e)); setBusy(false); }
  };

  return (
    <Modal wide title={`รายการที่สั่ง — ${po.po_no || po.supplier_ref || fmtDate(po.order_date)}`}
      sub="ใส่ราคาที่ตกลงไว้ด้วย จะได้เทียบกับ invoice ตอนของมาถึงได้ · ชิ้นต่อหน่วย = ถ้าสั่งเป็นกล่อง (เช่น 1 กล่อง = 6 ขวด)"
      onClose={onClose}>
      {err && <div className="ferr">{err}</div>}
      <div className="po-edit">
        <div className="po-edit-h">
          <span>รหัสสินค้า</span><span>ชื่อ / รายละเอียด</span><span>จำนวน</span><span>หน่วย</span>
          <span>ชิ้นต่อหน่วย</span><span>ราคา/หน่วย</span><span/>
        </div>
        {rows.map(r => (
          <div className="po-edit-r" key={r._k}>
            <input value={r.sku} onChange={set(r._k, 'sku')} placeholder="SP-1234"/>
            <input value={r.description} onChange={set(r._k, 'description')} placeholder="Land Nest Dome"/>
            <input type="number" min="0" step="any" value={r.qty} onChange={set(r._k, 'qty')}/>
            <input value={r.unit} onChange={set(r._k, 'unit')} placeholder="ชิ้น"/>
            <input type="number" min="1" step="1" value={r.pack_size} onChange={set(r._k, 'pack_size')}/>
            <input type="number" min="0" step="any" value={r.unit_price} onChange={set(r._k, 'unit_price')}/>
            <button className="po-x" title="ลบบรรทัดนี้" onClick={() => del(r._k)}>✕</button>
          </div>
        ))}
        <button className="po-addrow" onClick={() => setRows(rs => [...rs, blank()])}>＋ เพิ่มบรรทัด</button>
      </div>

      <div className="po-tot">
        รวม <b>{clean.length}</b> รายการ · <b>{qty(totPieces)}</b> ชิ้น
        {totValue > 0 && <> · มูลค่า <b>{money(totValue, currency)}</b></>}
      </div>

      <details className="po-paste">
        <summary>วางจาก Excel / ไฟล์ PI ทีเดียว</summary>
        <div className="po-paste-b">
          <div className="fhint">คัดลอกจาก Excel แล้ววางตรงนี้ได้เลย — 1 บรรทัดต่อ 1 รายการ เรียงว่า <b>รหัส, จำนวน, ราคา/หน่วย, ชิ้นต่อหน่วย, ชื่อ</b> (มีหัวตารางก็ได้ ระบบข้ามให้)</div>
          <textarea rows={5} value={paste} onChange={e => setPaste(e.target.value)}
            placeholder={'SP-1234\t100\t12000\t1\tLand Nest Dome\nNGN-32OZ\t20\t1800\t6\tWide Mouth 32oz (กล่องละ 6)'}/>
          <button className="btn2" onClick={applyPaste}>เติมลงตาราง</button>
        </div>
      </details>

      <div className="mfoot">
        <button className="btn2" onClick={onClose}>ยกเลิก</button>
        <button className="btn1" disabled={busy} onClick={submit}>{busy ? 'กำลังบันทึก…' : `บันทึก ${clean.length} รายการ`}</button>
      </div>
    </Modal>
  );
}


// ── โหมดตัวอย่าง: ใบจริงพอให้เห็นครบทุกสถานะ (ครบ/ขาด/เกิน/ยังไม่มา · ราคาตรง/ไม่ตรง/ยังไม่รู้) ──
function mockPo() {
  const L = (po_line_id, sku, description, oq, op, rp, ord_pack = 1, recv_pack = 1) => ({
    po_line_id, po_id: po_line_id.slice(0, 3), sku, description, sort_order: 0,
    ord_qty: oq, unit: ord_pack > 1 ? 'กล่อง' : 'ชิ้น', ord_pack,
    ord_unit_price: op, ord_pieces: oq * ord_pack, ord_price_pc: op == null ? null : op / ord_pack,
    recv_pieces: rp.reduce((t, x) => t + x.qty * recv_pack, 0),
    recv_price_pc_min: rp.length && rp.every(x => x.price != null) ? Math.min(...rp.map(x => x.price / recv_pack)) : null,
    recv_price_pc_max: rp.length && rp.every(x => x.price != null) ? Math.max(...rp.map(x => x.price / recv_pack)) : null,
    _lots: rp.map((x, i) => ({ id: `${po_line_id}-l${i}`, consignment_id: x.c, qty: x.qty,
      pack_size: recv_pack, unit_price: x.price, sku })),
  });
  const lines = [
    L('p1a', 'SP-1234', 'Land Nest Dome เต็นท์', 100, 12000, [{ c: 'c1', qty: 100, price: 12000 }]),
    L('p1b', 'SP-5678', 'IGT Table ชุดโต๊ะ', 50, 3000, [{ c: 'c1', qty: 30, price: 3400 }]),
    L('p1c', 'SP-9012', 'Titanium Mug 450', 200, 1450, []),
    L('p2a', 'NGN-32OZ', 'Wide Mouth 32oz (1 กล่อง = 6 ขวด)', 20, 1800, [{ c: 'c2', qty: 20, price: 1800 }], 6, 6),
    L('p2b', 'NGN-SILO', 'Silo 48oz', 30, 420, [{ c: 'c2', qty: 34, price: 420 }]),
    L('p2c', 'NGN-OTF', 'On-The-Fly 24oz', 40, null, [{ c: 'c2', qty: 40, price: null }]),
  ];
  const mk = (id, po_no, supplier_ref, order_date, expected_ship, season, status, keys) => {
    const ls = lines.filter(l => keys.includes(l.po_line_id)).map(l => ({ ...l, po_id: id }));
    const ord_pieces = ls.reduce((t, l) => t + l.ord_pieces, 0);
    const recv_pieces = ls.reduce((t, l) => t + l.recv_pieces, 0);
    return {
      id, po_no, supplier_ref, order_date, expected_ship, season, status,
      currency: 'JPY', incoterm: 'FOB', note: null, partner_id: 'demo',
      _lines: ls,
      sum: { po_id: id, ord_pieces, recv_pieces, pieces_left: Math.max(0, ord_pieces - recv_pieces),
        ord_value: ls.reduce((t, l) => t + l.ord_pieces * (l.ord_price_pc ?? 0), 0),
        qty_off: ls.filter(l => l.recv_pieces > 0 && l.recv_pieces !== l.ord_pieces).length,
        price_off: ls.filter(l => l.recv_price_pc_max != null && l.ord_price_pc != null
          && !near(l.recv_price_pc_max, l.ord_price_pc)).length },
    };
  };
  const pos = [
    mk('p1', 'PO-2568-014', 'PI-9931', '2025-09-12', '2026-03-31', 'FW25', 'open', ['p1a', 'p1b', 'p1c']),
    mk('p2', 'PO-2569-002', null, '2026-02-04', '2026-08-15', 'SS26', 'open', ['p2a', 'p2b', 'p2c']),
  ];
  const lineSt = pos.flatMap(p => p._lines);
  const recv = lineSt.flatMap(l => l._lots.map(t => ({ ...t, po_line_id: l.po_line_id, description: l.description })));
  const prices = [
    { partner_id: 'demo', sku: 'SP-5678', src: 'ordered', on_date: '2024-10-02', currency: 'JPY', price_pc: 2850 },
    { partner_id: 'demo', sku: 'SP-5678', src: 'invoiced', on_date: '2025-04-18', currency: 'JPY', price_pc: 2850 },
    { partner_id: 'demo', sku: 'SP-5678', src: 'ordered', on_date: '2025-09-12', currency: 'JPY', price_pc: 3000 },
    { partner_id: 'demo', sku: 'SP-5678', src: 'invoiced', on_date: '2026-07-11', currency: 'JPY', price_pc: 3400 },
  ];
  return { pos, lineSt, recv, prices };
}

// ── การ์ดหลัก ─────────────────────────────────────────────────────────────────
export default function PurchaseOrders({ partner, consignmentIds, shipLabel, canWrite, mock, onMsg, onFlash }) {
  const [pos, setPos] = useState([]);
  const [lineSt, setLineSt] = useState([]);
  const [recv, setRecv] = useState([]);
  const [prices, setPrices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState({});          // ใบไหนกางอยู่
  const [openLine, setOpenLine] = useState({});  // บรรทัดไหนกางดูล็อต/ประวัติราคา
  const [modal, setModal] = useState(null);      // {t:'po',item} | {t:'lines',po,lines}
  const [del, setDel] = useState(null);

  const pid = partner?.id ?? null;
  const consKey = (consignmentIds ?? []).join(',');
  const currency = (mock ? 'JPY' : partner?.default_currency) ?? null;
  const label = (id) => (mock ? ({ c1: 'SP-2601 · INV 24001', c2: 'NG-2609 · INV NG-118' })[id] ?? 'ตู้' : shipLabel?.(id) ?? 'ตู้');

  const load = async () => {
    if (mock) { const m = mockPo(); setPos(m.pos); setLineSt(m.lineSt); setRecv(m.recv); setPrices(m.prices); return; }
    if (!pid) { setPos([]); setLineSt([]); setRecv([]); setPrices([]); return; }
    setBusy(true);
    try {
      const [p, s, r, pp] = await Promise.all([
        db.listPurchaseOrders(pid), db.poLineStatus(pid),
        db.receivedLines(consignmentIds ?? []), db.pricePoints(pid),
      ]);
      setPos(p); setLineSt(s); setRecv(r); setPrices(pp);
    } catch (e) { onMsg?.(e.message ?? String(e)); }
    setBusy(false);
  };
  useEffect(() => { load(); }, [pid, consKey, mock]);

  const linesByPo = useMemo(() => {
    const m = {};
    for (const l of lineSt) (m[l.po_id] ??= []).push(l);
    return m;
  }, [lineSt]);
  const lotsByPoLine = useMemo(() => {
    const m = {};
    for (const r of recv) if (r.po_line_id) (m[r.po_line_id] ??= []).push(r);
    return m;
  }, [recv]);
  const priceBySku = useMemo(() => {
    const m = {};
    for (const p of prices) { const k = db.normSku(p.sku); if (k) (m[k] ??= []).push(p); }
    return m;
  }, [prices]);

  // ของที่มาถึงแต่ยังไม่รู้ว่าตรงกับใบสั่งไหน (รหัสไม่ตรง หรือยังไม่มีใบสั่ง)
  const orphan = useMemo(() => recv.filter(r => !r.po_line_id), [recv]);

  const totals = useMemo(() => {
    let left = 0, qtyOff = 0, priceOff = 0, openN = 0, value = 0;
    for (const p of pos) {
      if (p.status === 'cancelled') continue;
      const s = p.sum; if (!s) continue;
      if (p.status === 'open') { openN++; left += Number(s.pieces_left) || 0; }
      qtyOff += Number(s.qty_off) || 0; priceOff += Number(s.price_off) || 0;
      value += Number(s.ord_value) || 0;
    }
    return { left, qtyOff, priceOff, openN, value };
  }, [pos]);

  const savePo = async (fields, id) => {
    await db.savePurchaseOrder(id ? fields : { ...fields, partner_id: pid }, id);
    await load();
    onFlash?.(id ? 'บันทึกใบสั่งซื้อแล้ว' : 'สร้างใบสั่งซื้อแล้ว — กด “รายการที่สั่ง” เพื่อใส่ของที่สั่ง');
  };
  const saveLines = async (po, rows) => {
    await db.savePoLines(po.id, rows);
    const m = await db.matchPoLines(pid, consignmentIds ?? []);
    await load();
    onFlash?.(`บันทึกแล้ว${m.linked ? ` · จับคู่กับของที่มาถึงอัตโนมัติ ${m.linked} บรรทัด` : ''}`);
  };
  const remove = async (po) => {
    setDel(null);
    try { await db.deletePurchaseOrder(po.id); await load(); onFlash?.('ลบใบสั่งซื้อแล้ว'); }
    catch (e) { onMsg?.(e.message ?? String(e)); }
  };
  const rematch = async () => {
    setBusy(true);
    try {
      const m = await db.matchPoLines(pid, consignmentIds ?? []);
      await load();
      onFlash?.(m.linked ? `จับคู่เพิ่ม ${m.linked} บรรทัด` : 'ไม่มีบรรทัดใหม่ให้จับคู่ — รหัสสินค้าต้องตรงกันระหว่างใบสั่งกับ invoice');
    } catch (e) { onMsg?.(e.message ?? String(e)); }
    setBusy(false);
  };
  const editLines = (po) => setModal({ t: 'lines', po, lines: (linesByPo[po.id] ?? []).map(l => ({
    id: l.po_line_id, sku: l.sku, description: l.description, qty: l.ord_qty,
    unit: l.unit, pack_size: l.ord_pack, unit_price: l.ord_unit_price, note: null,
  })) });
  const setLotPrice = async (lot, field, v) => {
    try { await db.setLinePrice(lot.id, { [field]: v }); await load(); }
    catch (e) { onMsg?.(e.message ?? String(e)); }
  };

  return (
    <div className="bh-card bh-wide po-wrap">
      <div className="bh-card-h">
        <span className="bh-card-t">ใบสั่งซื้อ ↔ ของที่มาถึง ({pos.length})</span>
        <span className="bh-card-s">สั่งปีที่แล้วมาส่งปีนี้ก็ยังอยู่ — เทียบให้ว่ามาครบไม๊ ราคาตรงกับที่ตกลงไม๊ (เทียบที่ราคาต่อชิ้น)</span>
        {mock && <span className="bh-card-s">ตัวอย่าง — ของจริงดึงจากใบสั่งซื้อที่บันทึกไว้</span>}
        {canWrite && !mock && <>
          <button className="mini" disabled={busy || !pos.length} onClick={rematch}
            title="จับคู่ของที่มาถึงกับบรรทัดที่สั่ง โดยใช้รหัสสินค้า">⟲ จับคู่อัตโนมัติ</button>
          <button className="mini" onClick={() => setModal({ t: 'po', item: null })}>＋ ใบสั่งซื้อใหม่</button>
        </>}
      </div>

      {pos.length > 0 && (
        <div className="po-bar">
          <span className="po-b1">รอของอยู่ <b>{totals.openN}</b> ใบ</span>
          {totals.left > 0 && <span className="po-b2">ยังไม่มาอีก <b>{qty(totals.left)}</b> ชิ้น</span>}
          {totals.qtyOff > 0 && <span className="po-b3">จำนวนไม่ตรง <b>{totals.qtyOff}</b></span>}
          {totals.priceOff > 0 && <span className="po-b3">ราคาไม่ตรง <b>{totals.priceOff}</b></span>}
          {!totals.qtyOff && !totals.priceOff && <span className="po-bok">✓ ที่มาถึงตรงกับที่สั่งทั้งหมด</span>}
          {orphan.length > 0 && <span className="po-b4">ของที่มาถึงยังไม่รู้ว่าอยู่ใบไหน <b>{orphan.length}</b></span>}
        </div>
      )}

      {!pos.length ? (
        <div className="po-first">
          <div className="po-first-t">ยังไม่มีใบสั่งซื้อของแบรนด์นี้</div>
          <div className="po-first-s">
            ใส่ครั้งเดียวตอนสั่ง แล้วเวลาของมาถึง (ใส่รายการที่ “ใบข้อมูลสินค้า” ของตู้) ระบบจะจับคู่ด้วยรหัสสินค้าให้เอง
            แล้วบอกว่า <b>มาครบไม๊</b> และ <b>ราคาตรงกับที่ตกลงไม๊</b>
          </div>
          {canWrite && <button className="btn1" onClick={() => setModal({ t: 'po', item: null })}>＋ ใบสั่งซื้อใบแรก</button>}
        </div>
      ) : pos.map(p => {
        const s = p.sum ?? {};
        const ls = linesByPo[p.id] ?? [];
        const ordP = Number(s.ord_pieces) || 0;
        const recvP = Number(s.recv_pieces) || 0;
        const doneP = ordP ? Math.min(100, Math.round(recvP / ordP * 100)) : 0;
        const flags = (Number(s.qty_off) || 0) + (Number(s.price_off) || 0);
        const state = p.status === 'cancelled' ? 'void'
          : p.status === 'closed' ? 'done'
          : !ordP ? 'empty'
          : recvP === 0 ? 'wait'
          : recvP > ordP ? 'over'
          : recvP === ordP ? 'full' : 'part';
        const on = !!open[p.id];
        return (
          <div className={`po-card s-${state} ${on ? 'on' : ''}`} key={p.id}>
            <button className="po-h" onClick={() => setOpen(o => ({ ...o, [p.id]: !on }))}>
              <span className="po-dot"/>
              <span className="po-no">{p.po_no || p.supplier_ref || 'ไม่มีเลขที่'}</span>
              <span className="po-when">สั่ง {fmtDate(p.order_date)}{p.season ? ` · ${p.season}` : ''}</span>
              <span className="po-prog">
                <i style={{ width: `${doneP}%` }}/>
                <em>{ordP ? `${qty(recvP)}/${qty(ordP)} ชิ้น` : 'ยังไม่ได้ใส่รายการ'}</em>
              </span>
              <span className="po-tag">
                {state === 'void' ? 'ยกเลิก'
                  : state === 'done' ? '✓ ปิดแล้ว'
                  : state === 'empty' ? '＋ ใส่รายการ'
                  : state === 'full' ? '✓ มาครบ'
                  : state === 'over' ? `▲ มาเกิน ${qty(recvP - ordP)}`
                  : state === 'wait' ? '○ ยังไม่มา'
                  : `● ขาดอีก ${qty(ordP - recvP)}`}
              </span>
              {flags > 0 && <span className="po-flag">▲ ไม่ตรง {flags}</span>}
              <span className="po-val">{s.ord_value ? money(s.ord_value, p.currency ?? currency) : ''}</span>
              <span className="po-cv">{on ? '▲' : '▼'}</span>
            </button>

            {on && (
              <div className="po-body">
                <div className="po-meta">
                  {p.supplier_ref && p.po_no && <span>PI {p.supplier_ref}</span>}
                  {p.expected_ship && <span>นัดส่ง {fmtDate(p.expected_ship)}</span>}
                  {p.incoterm && <span>{p.incoterm}</span>}
                  {p.currency && <span>{p.currency}</span>}
                  <span>{STATUS_TH[p.status]}</span>
                  {p.note && <span className="po-note">{p.note}</span>}
                  {canWrite && !mock && <>
                    <button className="mini po-mr" onClick={() => editLines(p)}>✎ รายการที่สั่ง</button>
                    <button className="mini" onClick={() => setModal({ t: 'po', item: p })}>✎ แก้หัวใบ</button>
                  </>}
                </div>

                {!ls.length ? (
                  <div className="bh-empty">ยังไม่ได้ใส่ว่าสั่งอะไรบ้าง — กด “✎ รายการที่สั่ง” (วางจาก Excel ทีเดียวได้)</div>
                ) : (
                  <div className="po-tbl">
                    <div className="po-tr po-th">
                      <span>รหัส</span><span>รายการ</span><span>สั่ง</span><span>มาแล้ว</span>
                      <span>ราคาที่ตกลง</span><span>ราคาที่มาจริง</span><span>ผล</span>
                    </div>
                    {ls.map(l => {
                      const o = Number(l.ord_pieces) || 0, r = Number(l.recv_pieces) || 0;
                      const qState = r === 0 ? 'wait' : r > o ? 'over' : r === o ? 'ok' : 'part';
                      const op = l.ord_price_pc == null ? null : Number(l.ord_price_pc);
                      const rpMin = l.recv_price_pc_min == null ? null : Number(l.recv_price_pc_min);
                      const rpMax = l.recv_price_pc_max == null ? null : Number(l.recv_price_pc_max);
                      const pState = op == null ? 'noord' : rpMax == null ? 'norecv'
                        : (near(rpMin, op) && near(rpMax, op)) ? 'ok' : 'off';
                      const d = pState === 'off' ? pct(rpMax, op) : null;
                      const lots = lotsByPoLine[l.po_line_id] ?? [];
                      const hist = (priceBySku[db.normSku(l.sku)] ?? []).slice(0, 6);
                      const lo = !!openLine[l.po_line_id];
                      const cur = p.currency ?? currency;
                      return (
                        <div className="po-lwrap" key={l.po_line_id}>
                          <button className={`po-tr po-td q-${qState} p-${pState}`}
                            onClick={() => setOpenLine(x => ({ ...x, [l.po_line_id]: !lo }))}>
                            <span className="po-sku">{l.sku || '—'}</span>
                            <span className="po-nm">{l.description || '—'}
                              {Number(l.ord_pack) > 1 && <em> · {qty(l.ord_qty)} × {l.ord_pack} ชิ้น</em>}</span>
                            <span className="po-n">{qty(o)}</span>
                            <span className="po-n">{qty(r)}</span>
                            <span className="po-n">{op == null ? '—' : money(op, cur)}</span>
                            <span className="po-n">{rpMax == null ? '—'
                              : near(rpMin, rpMax) ? money(rpMax, cur) : `${money(rpMin, cur)}–${money(rpMax, cur)}`}</span>
                            <span className="po-res">
                              <b className={`m q-${qState}`}>{qState === 'ok' ? '✓ ครบ' : qState === 'wait' ? '○ ยังไม่มา'
                                : qState === 'over' ? `▲ เกิน ${qty(r - o)}` : `● ขาด ${qty(o - r)}`}</b>
                              <b className={`m p-${pState}`}>{pState === 'ok' ? '✓ ราคาตรง'
                                : pState === 'noord' ? '· ไม่รู้ราคาที่ตกลง'
                                : pState === 'norecv' ? '○ ยังไม่ใส่ราคา invoice'
                                : `▲ ${d > 0 ? '+' : ''}${d}%`}</b>
                            </span>
                          </button>

                          {lo && (
                            <div className="po-lot">
                              {lots.length ? lots.map(t => (
                                <div className="po-lot-r" key={t.id}>
                                  <span className="po-lot-s">{label(t.consignment_id)}</span>
                                  <span>{qty((Number(t.qty) || 0) * (Number(t.pack_size) || 1))} ชิ้น</span>
                                  {canWrite && !mock ? <>
                                    <label>ราคา/หน่วยใน invoice
                                      <input type="number" step="any" min="0" defaultValue={t.unit_price ?? ''}
                                        onBlur={e => { const v = e.target.value;
                                          if (String(t.unit_price ?? '') !== v) setLotPrice(t, 'unit_price', v); }}/>
                                    </label>
                                    <label>ชิ้นต่อหน่วย
                                      <input type="number" step="1" min="1" defaultValue={t.pack_size ?? 1}
                                        onBlur={e => { const v = e.target.value;
                                          if (String(t.pack_size ?? 1) !== v) setLotPrice(t, 'pack_size', v); }}/>
                                    </label>
                                  </> : <span>ราคา {money(t.unit_price, cur)}</span>}
                                  <span className="po-lot-pc">
                                    {t.unit_price == null ? 'ยังไม่ใส่ราคา'
                                      : `= ${money(Number(t.unit_price) / (Number(t.pack_size) || 1), cur)} ต่อชิ้น`}
                                  </span>
                                </div>
                              )) : <div className="po-lot-e">ยังไม่มีของล็อตไหนผูกกับบรรทัดนี้ — ของจะมาผูกเองเมื่อใส่รายการในตู้ด้วยรหัส <b>{l.sku || '(ยังไม่ได้ใส่รหัส)'}</b></div>}
                              {hist.length > 1 && (
                                <div className="po-hist">
                                  <span className="po-hist-l">เคยได้ราคานี้</span>
                                  {hist.map((h, i) => (
                                    <span className={`po-hist-c ${h.src}`} key={i}
                                      title={h.src === 'ordered' ? 'ราคาที่ตกลงในใบสั่งซื้อ' : 'ราคาบน invoice ที่มาถึง'}>
                                      {money(h.price_pc, h.currency ?? cur)}
                                      <em>{h.src === 'ordered' ? 'สั่ง' : 'inv'} {fmtDate(h.on_date)}</em>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {canWrite && !mock && (
                  <div className="po-foot">
                    <button className="po-del" onClick={() => setDel(p)}>ลบใบสั่งซื้อนี้</button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {orphan.length > 0 && (
        <details className="po-orphan">
          <summary>ของที่มาถึงแต่ยังไม่รู้ว่าอยู่ใบสั่งไหน ({orphan.length})</summary>
          <div className="po-orphan-b">
            <div className="fhint">รหัสสินค้าใน invoice ไม่ตรงกับในใบสั่งซื้อ (หรือยังไม่ได้สร้างใบสั่ง) — แก้รหัสให้ตรงแล้วกด “⟲ จับคู่อัตโนมัติ”</div>
            {orphan.slice(0, 40).map(o => (
              <div className="po-orphan-r" key={o.id}>
                <span className="po-sku">{o.sku || o.po_ref || '—'}</span>
                <span className="po-nm">{o.description || '—'}</span>
                <span className="po-n">{qty((Number(o.qty) || 0) * (Number(o.pack_size) || 1))} ชิ้น</span>
                <span className="po-lot-s">{label(o.consignment_id)}</span>
              </div>
            ))}
            {orphan.length > 40 && <div className="fhint">+ อีก {orphan.length - 40} รายการ</div>}
          </div>
        </details>
      )}

      {modal?.t === 'po' && (
        <PoModal item={modal.item} defCurrency={currency} onClose={() => setModal(null)}
          onSave={(f) => savePo(f, modal.item?.id)}
          onDelete={modal.item ? () => { setModal(null); setDel(modal.item); } : null}/>
      )}
      {modal?.t === 'lines' && (
        <PoLinesModal po={modal.po} lines={modal.lines} currency={modal.po.currency ?? currency}
          onClose={() => setModal(null)} onSave={(rows) => saveLines(modal.po, rows)}/>
      )}
      {del && (
        <Modal title="ลบใบสั่งซื้อนี้?" sub="รายการที่สั่งในใบนี้จะหายไปด้วย — ของที่มาถึงแล้วไม่หาย แค่เลิกผูกกัน" onClose={() => setDel(null)}>
          <p>{del.po_no || del.supplier_ref || 'ใบไม่มีเลขที่'} · สั่ง {fmtDate(del.order_date)}</p>
          <div className="mfoot">
            <button className="btn2" onClick={() => setDel(null)}>ไม่ลบ</button>
            <button className="btn-del" onClick={() => remove(del)}>ลบ</button></div>
        </Modal>
      )}
    </div>
  );
}
